import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

const ADDED_FEEDBACK_DURATION = 2000;

/**
 * Treatment guide for solution pages.
 *
 * Two jobs, both progressive enhancements over server-rendered markup:
 *  1. Filter the steps by treatment type when the modality tabs are shown.
 *  2. Add every product in the routine to the cart in one request.
 *
 * With JS off, every step is visible and each step's own add-to-cart form still works —
 * only the routine bar and the tabs go quiet.
 *
 * @typedef {object} TreatmentGuideRefs
 * @property {HTMLElement} [tablist] - Wrapper for the modality tabs.
 * @property {HTMLElement} steps - The ordered list of steps.
 * @property {HTMLButtonElement} [routineButton] - The "add all to cart" button.
 *
 * @extends {Component<TreatmentGuideRefs>}
 */
class TreatmentGuide extends Component {
  requiredRefs = ['steps'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #feedbackTimeout;

  /**
   * Filters the visible steps to a single treatment type.
   * @param {Event} event - The click event from a tab button.
   */
  selectModality(event) {
    const tab = /** @type {HTMLElement | null} */ (event.currentTarget);
    const { tablist, steps } = this.refs;
    if (!tab || !tablist) return;

    const selected = tab.dataset.modality ?? 'all';

    for (const button of tablist.querySelectorAll('[role="tab"]')) {
      button.setAttribute('aria-selected', String(button === tab));
    }

    for (const step of steps.children) {
      if (!(step instanceof HTMLElement)) continue;
      const matches = selected === 'all' || step.dataset.modality === selected;
      step.toggleAttribute('hidden', !matches);
    }
  }

  /**
   * Adds every buyable product in the guide to the cart in a single request, then hands
   * Horizon the same CartLinesUpdateEvent a product form would, so the cart drawer, the
   * cart bubble and any cart sections all refresh exactly as they normally do.
   */
  async addRoutine() {
    const button = this.refs.routineButton;
    if (!button || button.disabled) return;

    const variantIds = (button.dataset.variantIds ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (variantIds.length === 0) return;

    button.disabled = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: variantIds.map((id) => ({ merchandiseId: id, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      }).then((res) => res.json());

      const cart = await this.#refreshCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: Boolean(response.status),
          items: cart.items,
          source: 'treatment-guide',
          sourceId: this.dataset.sectionId ?? '',
          itemCount: variantIds.length,
        },
      });

      this.#showFeedback(button, response.status ? response.message : null);
    } catch (error) {
      deferred.reject(error);
      this.#showFeedback(button, null);
    }
  }

  /**
   * Restores the button after a short confirmation, or surfaces an error message.
   * @param {HTMLButtonElement} button - The routine button.
   * @param {string | null} errorMessage - An error to announce, if the add failed.
   */
  #showFeedback(button, errorMessage) {
    const original = button.dataset.originalLabel ?? button.textContent ?? '';
    button.dataset.originalLabel = original;
    button.textContent = errorMessage ?? Theme.translations?.added ?? 'Added';

    if (this.#feedbackTimeout) clearTimeout(this.#feedbackTimeout);
    this.#feedbackTimeout = setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
    }, ADDED_FEEDBACK_DURATION);
  }

  /**
   * Reads the current cart, preferring the live cart component when one is on the page.
   * @returns {Promise<any>} The cart payload.
   */
  async #refreshCart() {
    const cartItemsComponent = document.querySelector('cart-items-component');

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      // @ts-expect-error - fetchCartData is defined on CartItemsComponent
      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
      return response.json();
    });
  }
}

if (!customElements.get('treatment-guide')) {
  customElements.define('treatment-guide', TreatmentGuide);
}
